import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '../../assets/contexts/ThemeContext';
import './RelatedSitesCarousel.css';
import CustomSelect from '../../components/widgets/CustomSelect';

/**
 * 연관 사이트 자동 캐러셀 컴포넌트
 * - 자동 인터벌 방식으로 좌측으로 하나씩 이동하는 애니메이션
 * - 각 로고는 클릭하면 해당 사이트로 이동
 * - 좌우 화살표 버튼으로 수동 네비게이션
 * - 일시정지/재생 버튼
 * - 두 가지 유형 지원: 이미지썸네일, 기본콤보형
 *
 * @param {Object} data - 컴포넌트 데이터
 * @param {string} data.title - 섹션 제목 (기본값: "연관 사이트")
 * @param {Array} data.sites - 사이트 리스트
 * @param {string} data.sites[].image - 로고 이미지 경로
 * @param {string} data.sites[].url - 사이트 링크 URL
 * @param {string} data.sites[].name - 사이트 이름 (alt, title 속성)
 * @param {number} data.interval - 자동 이동 간격 (밀리초, 기본값: 3000)
 * @param {number} data.itemsPerView - 한 번에 보여줄 아이템 수 (기본값: 5)
 * @param {boolean} data.autoPlay - 자동 재생 여부 (기본값: true)
 */
const RelatedSitesCarousel = ({ data }) => {
  // ThemeContext에서 연관사이트 설정 가져오기 (React Context 직접 사용)
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || (typeof window !== 'undefined' && window.__themeConfig) || {};

  const relatedSitesEnabled = themeConfig.relatedSitesEnabled !== false; // 기본값: true
  const relatedSitesType = themeConfig.relatedSitesType || 'image-thumbnail';
  const relatedSitesShowTitle = themeConfig.relatedSitesShowTitle !== false; // 기본값: true
  const relatedSitesShowImage = themeConfig.relatedSitesShowImage !== false; // 기본값: true
  const relatedSitesShowUrl = themeConfig.relatedSitesShowUrl === true; // 기본값: false
  const relatedSitesListExpanded = themeConfig.relatedSitesListExpanded === true; // 기본값: false
  const [isListExpanded, setIsListExpanded] = useState(relatedSitesListExpanded);

  // themeConfig의 리스트 펼침 상태 변경 감지
  useEffect(() => {
    setIsListExpanded(relatedSitesListExpanded);
  }, [relatedSitesListExpanded]);
  const title = data?.title || '연관 사이트';
  const sites = useMemo(() => data?.sites || [], [data]);
  const [comboUrl, setComboUrl] = useState('');
  const totalItems = sites.length;

  // 무한 루프를 위해 아이템을 3배로 복제
  // [복제본1: sites][원본: sites][복제본2: sites]
  const displaySites = useMemo(() => [...sites, ...sites, ...sites], [sites]);

  // 초기 인덱스를 복제된 아이템의 중간(원본 시작 위치)으로 설정
  const initialIndex = totalItems > 0 ? totalItems : 0;
  const autoPlay = data?.autoPlay !== undefined ? data.autoPlay : true; // 기본값: 자동 재생
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPaused, setIsPaused] = useState(!autoPlay); // autoPlay가 false면 초기 상태를 일시정지로
  const [imageErrors, setImageErrors] = useState({}); // 이미지 로드 실패 추적
  const [isTransitioning, setIsTransitioning] = useState(true); // transition 상태 관리
  /** 767px 이하: 캐러셀 대신 전체 로고 그리드 (제목·일시정지·좌우 버튼 숨김) */
  const [isNarrowMobile, setIsNarrowMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsNarrowMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const intervalRef = useRef(null);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const currentIndexRef = useRef(initialIndex); // currentIndex를 ref로도 추적
  const itemRefs = useRef([]); // 각 아이템의 ref를 저장
  const itemWidths = useRef([]); // 각 아이템의 실제 너비를 저장
  const itemPositions = useRef([]); // 각 아이템의 left 위치를 저장

  const interval = data?.interval || 3000; // 기본 3초
  const defaultLogo = '/assets/images/logo/logo.png'; // 기본 로고 이미지

  // 이미지 로드 실패 핸들러
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleImageError = (siteUrl) => {
    setImageErrors((prev) => ({
      ...prev,
      [siteUrl]: true
    }));
  };

  // 이미지 소스 결정 (에러 발생 시 기본 로고 사용)
  const getImageSrc = (site) => {
    if (imageErrors[site.url]) {
      return defaultLogo;
    }
    return site.image || defaultLogo;
  };

  // currentIndex 변경 시 ref 업데이트
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  // translateX 계산: currentIndex에 해당하는 아이템의 left를 컨테이너 왼쪽에 맞춤
  const calculateTranslateX = useCallback(() => {
    if (itemPositions.current.length === 0 || !containerRef.current) return 0;
    if (currentIndex < 0 || currentIndex >= itemPositions.current.length) return 0;

    // currentIndex에 해당하는 아이템의 left 위치
    const targetItemLeft = itemPositions.current[currentIndex] || 0;

    // 컨테이너의 패딩 고려 (10px)
    const containerPadding = 10;

    // 해당 아이템의 left를 컨테이너 왼쪽(패딩 포함)에 맞추기 위해 음수로 이동
    return -targetItemLeft + containerPadding;
  }, [currentIndex]);

  // 각 아이템의 실제 너비와 위치 측정
  useEffect(() => {
    if (isNarrowMobile || !trackRef.current || displaySites.length === 0) return;

    const measureItems = () => {
      requestAnimationFrame(() => {
        const widths = [];
        const positions = [];
        let cumulativeLeft = 0;

        itemRefs.current.forEach((itemRef, index) => {
          if (itemRef) {
            const rect = itemRef.getBoundingClientRect();

            widths[index] = rect.width;
            // 트랙 기준 상대 위치 계산
            positions[index] = cumulativeLeft;
            cumulativeLeft += rect.width;
          }
        });

        if (widths.length > 0 && widths.every(w => w > 0)) {
          itemWidths.current = widths;
          itemPositions.current = positions;
        }
      });
    };

    measureItems();

    // 이미지 로드 후 재측정
    const images = trackRef.current.querySelectorAll('.related-sites-logo');
    const handleImageLoad = () => {
      setTimeout(measureItems, 50);
    };

    images.forEach(img => {
      if (img.complete) {
        handleImageLoad();
      } else {
        img.addEventListener('load', handleImageLoad, { once: true });
        img.addEventListener('error', handleImageLoad, { once: true });
      }
    });

    return () => {
      images.forEach(img => {
        img.removeEventListener('load', handleImageLoad);
        img.removeEventListener('error', handleImageLoad);
      });
    };
  }, [displaySites.length, relatedSitesShowTitle, isNarrowMobile]);

  // 자동 이동 애니메이션
  useEffect(() => {
    if (isNarrowMobile) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    // sites가 없거나 일시정지 상태면 자동 재생 중지
    // autoPlay는 초기 상태만 결정하고, 이후에는 isPaused 상태에 따라 동작
    if (sites.length === 0 || isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // 다음 인덱스로 이동
        if (prevIndex < displaySites.length - 1) {
          return prevIndex + 1;
        } else {
          // 마지막 아이템이면 첫 번째로 (무한 루프)
          return 0;
        }
      });
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites.length, interval, isPaused, displaySites.length, isNarrowMobile]);

  // 수동 네비게이션: currentIndex 기준으로 이동
  const handlePrev = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex > 0) {
        return prevIndex - 1;
      } else {
        // 첫 번째 아이템이면 마지막으로 (무한 루프)
        return displaySites.length - 1;
      }
    });
    // autoPlay가 true일 때만 수동 조작 시 자동 재생 일시정지 후 재시작
    if (autoPlay) {
      setIsPaused(true);
      setTimeout(() => setIsPaused(false), interval * 2);
    }
  }, [displaySites.length, autoPlay, interval]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex < displaySites.length - 1) {
        return prevIndex + 1;
      } else {
        // 마지막 아이템이면 첫 번째로 (무한 루프)
        return 0;
      }
    });
    // autoPlay가 true일 때만 수동 조작 시 자동 재생 일시정지 후 재시작
    if (autoPlay) {
      setIsPaused(true);
      setTimeout(() => setIsPaused(false), interval * 2);
    }
  }, [displaySites.length, autoPlay, interval]);

  const handleTogglePause = () => {
    setIsPaused((prev) => !prev);
  };

  // 무한 루프를 위한 인덱스 경계 처리
  useEffect(() => {
    if (isNarrowMobile || totalItems === 0 || !trackRef.current) return;

    const trackElement = trackRef.current;

    // transition 완료 후 경계 처리
    const handleTransitionEnd = (e) => {
      // transform transition만 처리
      if (e.propertyName !== 'transform' || e.target !== trackElement) return;

      const currentIdx = currentIndexRef.current;

      // 마지막 복제 아이템을 넘어가면 실제 첫 번째로 점프
      if (currentIdx >= totalItems * 2) {
        setIsTransitioning(false);
        // transition 없이 즉시 점프
        requestAnimationFrame(() => {
          setCurrentIndex(currentIdx - totalItems);
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }
      // 첫 번째 복제 아이템 이전으로 가면 실제 마지막으로 점프
      else if (currentIdx < 0) {
        setIsTransitioning(false);
        // transition 없이 즉시 점프
        requestAnimationFrame(() => {
          setCurrentIndex(currentIdx + totalItems * 2);
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }
    };

    trackElement.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      trackElement.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [totalItems, isNarrowMobile]);


  // 연관사이트 영역이 비활성화되어 있으면 렌더링하지 않음
  if (!relatedSitesEnabled) {
    return null;
  }

  if (sites.length === 0) {
    return null;
  }

  // 기본콤보형 렌더링 (셀렉트 박스만)
  if (relatedSitesType === 'combo') {
    return (
      <>
        <div className="related-sites-combo">
          <div className="related-sites-combo-wrapper">
            <h3 className="related-sites-title">{title}</h3>
            <CustomSelect
              className="related-sites-combo-select"
              classNamePrefix="related-sites-combo-select"
              value={comboUrl}
              onChange={(v) => {
                if (v) {
                  window.open(v, '_blank', 'noopener,noreferrer');
                  setComboUrl('');
                }
              }}
              isSearchable={false}
              isClearable={false}
              options={[
                { value: '', label: '연관 사이트 선택' },
                ...sites.map((site, index) => ({
                  value: site.url,
                  label: site.name || site.url || `site-${index + 1}`,
                })),
              ]}
            />
          </div>
        </div>
        <style>{`
          .related-sites-section {
            width: 100%;
            border-top: 1px solid #e9ecef;
            border-bottom: 1px solid #e9ecef;
          }

          .related-sites-combo {
            width: 100%;
            padding: 20px 0;
            background-color: #fff;
          }

          .related-sites-combo-wrapper {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 0 15px;
          }

          .related-sites-title {
            font-size: 1.25rem;
            font-weight: 700;
            color: #333;
            margin: 0;
            white-space: nowrap;
            flex-shrink: 0;
          }

          .related-sites-combo-select {
            flex: 1;
          }
          .related-sites-combo-select .related-sites-combo-select__control {
            min-height: 40px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            background-color: #fff;
            cursor: pointer;
          }
          .related-sites-combo-select .related-sites-combo-select__control:hover {
            border-color: #999;
          }
          .related-sites-combo-select .related-sites-combo-select__value-container {
            padding: 0 12px;
          }
          .related-sites-combo-select .related-sites-combo-select__indicators {
            height: 40px;
          }

          .related-sites-combo-list {
            padding: 0 15px;
            display: flex;
            flex-direction: column;
            background-color: #fff;
            border: 1px solid #e9ecef;
            border-radius: 4px;
            overflow: hidden;
          }

          .related-sites-combo-item {
            border-bottom: 1px solid #e9ecef;
          }

          .related-sites-combo-item:last-child {
            border-bottom: none;
          }

          .related-sites-combo-link {
            display: flex;
            align-items: center;
            gap: 15px;
            text-decoration: none;
            color: #333;
            padding: 12px 15px;
            transition: background-color 0.2s;
            width: 100%;
            box-sizing: border-box;
          }

          .related-sites-combo-link:hover {
            background-color: #f8f9fa;
          }

          .related-sites-combo-image-wrapper {
            flex-shrink: 0;
            width: 48px;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #fff;
            border: 1px solid #e9ecef;
            border-radius: 4px;
            padding: 4px;
          }

          .related-sites-combo-image {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
          }

          .related-sites-combo-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;
            min-width: 0;
          }

          .related-sites-combo-name {
            font-size: 14px;
            font-weight: 500;
            color: #333;
            line-height: 1.4;
            word-break: break-word;
          }

          .related-sites-combo-url {
            font-size: 12px;
            color: #666;
            line-height: 1.3;
            word-break: break-all;
          }

          @media (max-width: 768px) {
            .related-sites-combo-wrapper {
              flex-direction: column;
              align-items: flex-start;
            }

            .related-sites-combo-select {
              width: 100%;
            }
          }
        `}</style>
      </>
    );
  }

  // 리스트형 렌더링 (토글 가능한 리스트)
  if (relatedSitesType === 'list') {
    const toggleList = () => {
      const newExpanded = !isListExpanded;
      setIsListExpanded(newExpanded);
      // themeConfig에도 반영
      if (typeof window !== 'undefined' && window.__ThemeContextInstance) {
        const themeContext = window.__ThemeContextInstance;
        if (themeContext && themeContext.set) {
          themeContext.set('relatedSitesListExpanded', newExpanded);
        }
      }
    };

    return (
      <>
        <div className="related-sites-list">
          <div className="related-sites-list-header" onClick={toggleList}>
            <h3 className="related-sites-title">{title}</h3>
            <button
              type="button"
              className="related-sites-toggle-btn"
              aria-label={isListExpanded ? '접기' : '펼치기'}
            >
              {isListExpanded ? '▲' : '▼'}
            </button>
          </div>
          {isListExpanded && sites.length > 0 && (
            <div className="related-sites-list-content">
              {sites.map((site, index) => (
                <div key={`list-item-${index}-${site.url}`} className="related-sites-list-item">
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="related-sites-list-link"
                    title={site.name || site.url}
                  >
                    {relatedSitesShowImage && (
                      <div className="related-sites-list-image-wrapper">
                        <img
                          src={getImageSrc(site)}
                          alt={site.name || '연관 사이트'}
                          className="related-sites-list-image"
                          onError={() => handleImageError(site.url, site.image)}
                        />
                      </div>
                    )}
                    <div className="related-sites-list-content-wrapper">
                      <span className="related-sites-list-name">{site.name || site.url}</span>
                      {relatedSitesShowUrl && (
                        <span className="related-sites-list-url">{site.url}</span>
                      )}
                    </div>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
        <style>{`
          .related-sites-section {
            width: 100%;
            border-top: 1px solid #e9ecef;
            border-bottom: 1px solid #e9ecef;
          }

          .related-sites-list {
            width: 100%;
            padding: 20px 0;
            background-color: #fff;
          }

          .related-sites-list-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 0 15px;
            cursor: pointer;
            user-select: none;
          }

          .related-sites-list-header:hover {
            background-color: #f8f9fa;
            border-radius: 4px;
          }

          .related-sites-title {
            font-size: 1.25rem;
            font-weight: 700;
            color: #333;
            margin: 0;
            white-space: nowrap;
            flex-shrink: 0;
          }

          .related-sites-toggle-btn {
            width: 32px;
            height: 32px;
            border: 1px solid #ddd;
            background-color: #fff;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 14px;
            color: #666;
            transition: all 0.3s ease;
            padding: 0;
            line-height: 1;
            flex-shrink: 0;
          }

          .related-sites-toggle-btn:hover {
            background-color: #f5f5f5;
            border-color: #999;
            color: #333;
          }

          .related-sites-list-content {
            padding: 15px;
            display: flex;
            flex-direction: column;
            background-color: #fff;
            border: 1px solid #e9ecef;
            border-radius: 4px;
            margin: 15px 15px 0 15px;
            overflow: hidden;
            animation: slideDown 0.3s ease-out;
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              max-height: 0;
            }
            to {
              opacity: 1;
              max-height: 1000px;
            }
          }

          .related-sites-list-item {
            border-bottom: 1px solid #e9ecef;
          }

          .related-sites-list-item:last-child {
            border-bottom: none;
          }

          .related-sites-list-link {
            display: flex;
            align-items: center;
            gap: 15px;
            text-decoration: none;
            color: #333;
            padding: 12px 15px;
            transition: background-color 0.2s;
            width: 100%;
            box-sizing: border-box;
          }

          .related-sites-list-link:hover {
            background-color: #f8f9fa;
          }

          .related-sites-list-image-wrapper {
            flex-shrink: 0;
            width: 48px;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #fff;
            border: 1px solid #e9ecef;
            border-radius: 4px;
            padding: 4px;
          }

          .related-sites-list-image {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
          }

          .related-sites-list-content-wrapper {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;
            min-width: 0;
          }

          .related-sites-list-name {
            font-size: 14px;
            font-weight: 500;
            color: #333;
            line-height: 1.4;
            word-break: break-word;
          }

          .related-sites-list-url {
            font-size: 12px;
            color: #666;
            line-height: 1.3;
            word-break: break-all;
          }

          @media (max-width: 768px) {
            .related-sites-list-header {
              padding: 0 10px;
            }

            .related-sites-list-content {
              margin: 10px 10px 0 10px;
              padding: 10px;
            }
          }
        `}</style>
      </>
    );
  }

  // 이미지썸네일 유형 렌더링 (기본)
  // displaySites는 이미 상단에서 정의됨

  // 767px 이하: 제목·일시정지·좌우 이동 숨김, 로고 전체 노출·축소 그리드
  if (isNarrowMobile) {
    return (
      <>
        <div
          className="related-sites-carousel related-sites-carousel--narrow-all"
          role="region"
          aria-label={title}
        >
          <ul className="related-sites-narrow-grid">
            {sites.map((site, index) => (
              <li key={`narrow-${index}-${site.url || index}`} className="related-sites-narrow-cell">
                <a
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="related-sites-narrow-link"
                  title={site.name || site.url}
                >
                  <span className="related-sites-narrow-logo-wrap">
                    <img
                      src={getImageSrc(site)}
                      alt=""
                      className="related-sites-narrow-logo"
                      onError={() => handleImageError(site.url)}
                    />
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
        <style>{`
          .related-sites-section {
            width: 100%;
            border-top: 1px solid #e9ecef;
            border-bottom: 1px solid #e9ecef;
          }
          .related-sites-carousel.related-sites-carousel--narrow-all {
            width: 100%;
            padding: 10px 0 12px;
            background-color: #fff;
          }
          .related-sites-narrow-grid {
            list-style: none;
            margin: 0;
            padding: 8px 10px 10px;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: stretch;
            gap: 8px 10px;
            box-sizing: border-box;
          }
          .related-sites-narrow-cell {
            flex: 0 1 auto;
            min-width: 0;
            max-width: calc(33.333% - 7px);
          }
          @media (max-width: 380px) {
            .related-sites-narrow-cell {
              max-width: calc(50% - 6px);
            }
          }
          .related-sites-narrow-link {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-decoration: none;
            color: #333;
            padding: 8px 6px;
            border-radius: 8px;
            border: 1px solid #e9ecef;
            background: #fff;
            box-sizing: border-box;
            min-height: 100%;
            transition: background-color 0.2s ease, border-color 0.2s ease;
          }
          .related-sites-narrow-link:active {
            background-color: #f3f4f6;
          }
          .related-sites-narrow-logo-wrap {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 32px;
          }
          .related-sites-narrow-logo {
            max-width: 100%;
            max-height: 28px;
            width: auto;
            height: auto;
            object-fit: contain;
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      <div className="related-sites-carousel">
        <div className="related-sites-wrapper">
          <h3 className="related-sites-title">{title}</h3>
          <button
            type="button"
            className={`related-sites-btn related-sites-btn-pause ${isPaused ? 'paused' : ''}`}
            onClick={handleTogglePause}
            aria-label={isPaused ? '재생' : '일시정지'}
          >
            {isPaused ? '▶' : '■'}
          </button>
          <button
            type="button"
            className="related-sites-btn related-sites-btn-prev"
            onClick={handlePrev}
            aria-label="이전"
          >
            &lt;
          </button>
          <div className="related-sites-container" ref={containerRef}>
            <div
              ref={trackRef}
              className="related-sites-track"
              style={{
                transform: `translateX(${calculateTranslateX()}px)`,
                transition: (isPaused || !isTransitioning) ? 'none' : 'transform 0.6s ease-in-out',
              }}
            >
              {displaySites.map((site, index) => (
                <div
                  key={`carousel-item-${index}-${site.url}`}
                  ref={(el) => {
                    if (el) itemRefs.current[index] = el;
                  }}
                  className="related-sites-item"
                >
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="related-sites-link"
                    title={site.name || site.url}
                  >
                    <img
                      src={getImageSrc(site)}
                      alt={site.name || '연관 사이트'}
                      className="related-sites-logo"
                      onError={() => handleImageError(site.url, site.image)}
                    />
                    {relatedSitesShowTitle && site.name && (
                      <span className="related-sites-name">{site.name}</span>
                    )}
                  </a>
                </div>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="related-sites-btn related-sites-btn-next"
            onClick={handleNext}
            aria-label="다음"
          >
            &gt;
          </button>
        </div>
      </div>
      <style>{`
        .related-sites-section {
          width: 100%;
          border-top: 1px solid #e9ecef;
          border-bottom: 1px solid #e9ecef;
        }

        /* 이미지썸네일 유형 - 캐러셀 컨테이너 */
        .related-sites-carousel {
          width: 100%;
          padding: 8px 0;
          background-color: #fff;
        }

        /* 이미지썸네일 유형 - 래퍼 */
        .related-sites-carousel .related-sites-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 0 15px;
        }

        /* 이미지썸네일 유형 - 제목 */
        .related-sites-carousel .related-sites-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #333;
          margin: 0;
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* 이미지썸네일 유형 - 버튼 공통 */
        .related-sites-carousel .related-sites-btn {
          width: 36px;
          height: 36px;
          border: 1px solid #ddd;
          background-color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 16px;
          color: #666;
          transition: all 0.3s ease;
          padding: 0;
          line-height: 1;
          flex-shrink: 0;
        }

        .related-sites-carousel .related-sites-btn:hover {
          background-color: #f5f5f5;
          border-color: #999;
          color: #333;
        }

        .related-sites-carousel .related-sites-btn:active {
          transform: scale(0.95);
        }

        /* 이미지썸네일 유형 - 일시정지 버튼 */
        .related-sites-carousel .related-sites-btn-pause.paused {
          background-color: #4CAF50;
          color: #fff;
          border-color: #4CAF50;
        }

        /* 이미지썸네일 유형 - 컨테이너 */
        .related-sites-carousel .related-sites-container {
          flex: 1;
          overflow: hidden;
          position: relative;
          min-width: 0;
          padding: 0px;
          box-sizing: border-box;
        }

        /* 이미지썸네일 유형 - 트랙 */
        .related-sites-carousel .related-sites-track {
          display: flex;
          align-items: flex-start;
          will-change: transform;
        }

        /* 이미지썸네일 유형 - 아이템 */
        .related-sites-carousel .related-sites-item {
          flex-shrink: 0;
          width: auto;
          min-width: 0;
          padding: 0 10px;
          box-sizing: border-box;
          display: flex;
          align-items: flex-start;
          justify-content: center;
        }

        /* 이미지썸네일 유형 - 링크 */
        .related-sites-carousel .related-sites-link {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          text-decoration: none;
          color: #333;
          transition: transform 0.3s ease;
          padding: 15px;
          border-radius: 8px;
          width: fit-content;
          max-width: 100%;
        }

        .related-sites-carousel .related-sites-link:hover {
          transform: translateY(-5px);
          background-color: #f9f9f9;
        }

        /* 이미지썸네일 유형 - 로고 */
        .related-sites-carousel .related-sites-logo {
          max-width: 200px;
          width: auto;
          height: 40px;
          object-fit: contain;
          margin-bottom: 8px;
          flex-shrink: 0;
        }

        /* 이미지썸네일 유형 - 이름 */
        .related-sites-carousel .related-sites-name {
          font-size: 0.875rem;
          color: #666;
          text-align: center;
          line-height: 1.4;
          word-break: keep-all;
          ${relatedSitesShowTitle ? 'min-height: auto;' : 'display: none;'}
          display: ${relatedSitesShowTitle ? 'flex' : 'none'};
          align-items: flex-start;
          justify-content: center;
        }

        /* 반응형 디자인 - 태블릿 */
        @media (max-width: 1400px) {
          .related-sites-carousel .related-sites-item {
            padding: 0 8px;
          }
        }

        @media (max-width: 992px) {
          .related-sites-carousel .related-sites-title {
            font-size: 1.1rem;
          }

          .related-sites-carousel .related-sites-btn {
            width: 32px;
            height: 32px;
            font-size: 14px;
          }

          .related-sites-carousel .related-sites-logo {
            height: 50px;
            max-width: 180px;
          }

          .related-sites-carousel .related-sites-name {
            font-size: 0.8rem;
          }
        }

        /* 반응형 디자인 - 모바일 */
        @media (max-width: 768px) {
          .related-sites-carousel .related-sites-wrapper {
            gap: 8px;
            padding: 0 10px;
          }

          .related-sites-carousel .related-sites-title {
            font-size: 1rem;
          }

          .related-sites-carousel .related-sites-item {
            padding: 0 6px;
            width: auto;
          }

          .related-sites-carousel .related-sites-link {
            padding: 10px 4px;
          }

          .related-sites-carousel .related-sites-logo {
            height: 80px;
            margin-bottom: 0;
            width: auto;
            max-width: 150px;
            object-fit: contain;
          }

          .related-sites-carousel .related-sites-name {
            display: none;
          }
        }

        /* 반응형 디자인 - 작은 모바일 */
        @media (max-width: 576px) {
          .related-sites-carousel {
            padding: 15px 0;
          }

          .related-sites-carousel .related-sites-wrapper {
            gap: 6px;
            padding: 0 8px;
          }

          .related-sites-carousel .related-sites-title {
            font-size: 0.9rem;
          }

          .related-sites-carousel .related-sites-btn {
            width: 28px;
            height: 28px;
            font-size: 12px;
          }

          .related-sites-carousel .related-sites-item {
            padding: 0 4px;
            width: auto;
          }

          .related-sites-carousel .related-sites-link {
            padding: 8px 2px;
          }

          .related-sites-carousel .related-sites-logo {
            height: 70px;
            margin-bottom: 0;
            width: auto;
            max-width: 120px;
            object-fit: contain;
          }

          .related-sites-carousel .related-sites-name {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default RelatedSitesCarousel;
